/**
 * SeoHead — Centralized SEO meta tag renderer
 *
 * Single source of truth for <title>, meta description, canonical,
 * Open Graph, and hreflang alternate tags. Prevents:
 *   - React 19 <title> array children bug (uses template literals)
 *   - Inconsistent site name suffix
 *   - Missing og:* tags for social sharing
 *   - Drift between loader return types and rendered meta tags
 *
 * Usage:
 *   Static page:  <SeoHead title="About" />
 *   Dynamic page:  <SeoHead {...seo} />
 *   Home page:    <SeoHead title="LetsGetHome — ..." noSuffix />
 */

import { useLocale } from '@/shared/i18n';
import { BRAND_NAME, TITLE_SEPARATOR_BY_LOCALE } from '@letsgethome/lib/constants';
import { robotsDirectiveLabel } from '@letsgethome/lib/proto-enums/labels_gen';
import { assertNever } from '@letsgethome/lib/utils/enums';
import { DEFAULT_LOCALE, plainText } from '@letsgethome/types/branded';

import type { RobotsPolicy, SeoMeta } from '../types/seo';

// Re-exported for back-compat: SeoMeta is now owned by the `types` layer
// (see ../types/seo) so lib/hooks can import the contract without a cycle.
export type { SeoMeta };

// The brand name is the Rust SSOT (`domain_core::presentation::BRAND_NAME`
// via domain-wasm) — the same constant the backend SEO module renders with.
const SITE_NAME = BRAND_NAME;

/**
 * `RobotsPolicy` → `<meta name="robots">` content, or `null` for the indexable
 * default (no tag). Exhaustive with a `never` guard so a new policy variant is a
 * compile error until its rendering is decided (ADR 0142 META-10).
 *
 * The directive STRINGS come from the generated `RobotsDirective` label
 * projection (proto-enums.yaml SSOT) — the same declaration the backend
 * vocabulary carries — instead of fresh literals that could drift (R5 PR-5).
 * The labels are locale-invariant (a robots meta is not UI copy), so the
 * source locale is used.
 */
function robotsContent(policy: RobotsPolicy): string | null {
  switch (policy) {
    case 'index':
      return null;
    case 'noindex-follow':
      return robotsDirectiveLabel('NOINDEX_FOLLOW', 'en');
    case 'noindex-nofollow':
      return robotsDirectiveLabel('NOINDEX_NOFOLLOW', 'en');
    default:
      return assertNever(policy);
  }
}

/**
 * The OG + Twitter image metas (image, alt, width, height). Extracted from
 * `SeoHead` so the image-presence/alt/dimension gating lives in one place and
 * keeps the parent under the cyclomatic-complexity budget. Renders nothing
 * without an image; `og:image:alt`/`twitter:image:alt` only alongside alt text
 * (G5); `og:image:width`/`height` only when the proto carried real (>0) dims
 * (META-9).
 */
type ImageMeta = Pick<SeoMeta, 'ogImage' | 'ogImageAlt' | 'ogImageWidth' | 'ogImageHeight'>;

function SeoImageMeta({ image }: { image: ImageMeta }) {
  const { ogImage, ogImageAlt, ogImageWidth, ogImageHeight } = image;
  if (!ogImage) return null;
  const showDims = !!ogImageWidth && !!ogImageHeight;
  return (
    <>
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:image" content={ogImage} />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      {ogImageAlt && <meta name="twitter:image:alt" content={ogImageAlt} />}
      {showDims && <meta property="og:image:width" content={String(ogImageWidth)} />}
      {showDims && <meta property="og:image:height" content={String(ogImageHeight)} />}
    </>
  );
}

interface SeoHeadProps extends SeoMeta {
  noSuffix?: boolean;
}

// eslint-disable-next-line @letsgethome/max-component-props -- centralized SEO passthrough: the props ARE the SeoMeta contract (../types/seo), spread once per page (ADR 0142); same exemption as the responsive-variant passthrough components
export function SeoHead({
  title,
  description,
  canonical,
  ogImage,
  ogImageAlt,
  ogImageWidth,
  ogImageHeight,
  ogTitle,
  robots,
  alternates,
  ogType,
  locale,
  structuredData,
  noSuffix,
}: SeoHeadProps) {
  // Suffix contract (SSOT-W1): backend-built titles are FINAL — since the
  // truncate-then-brand door they always carry the brand within budget, and
  // `seoFromProto` marks them `noSuffix` explicitly. Only static pages (whose
  // `title` is a bare page name from `t()`) get the suffix, composed from the
  // generated separator+brand consts — the old `title.includes(SITE_NAME)`
  // sniff and the drifted `seo.titleTemplate` catalog key are retired.
  const pageLocale = useLocale();
  // `plainText` remint: `title` is already LocalizedText and the separator +
  // brand are generated consts, so the composition carries no untranslated
  // copy. The separator comes from the generated TITLE_SEPARATOR_BY_LOCALE
  // record — the projection of the ONE Rust dispatch
  // (domain_core::presentation::title_separator), so web and backend cannot
  // disagree on which locale gets which separator (indexing by `Locale` also
  // fails to compile if the record ever misses a site locale).
  const fullTitle = noSuffix
    ? title
    : plainText(`${title}${TITLE_SEPARATOR_BY_LOCALE[pageLocale]}${SITE_NAME}`);
  const socialTitle = ogTitle ?? fullTitle;
  // `undefined` policy ⇒ indexable default (no robots tag).
  const robotsTag = robots ? robotsContent(robots) : null;

  return (
    <>
      {/* eslint-disable-next-line no-restricted-syntax -- Centralized SEO component */}
      <title>{fullTitle}</title>
      {/* eslint-disable-next-line no-restricted-syntax -- Centralized SEO component */}
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {robotsTag && <meta name="robots" content={robotsTag} />}

      {/* OpenGraph */}
      <meta property="og:type" content={ogType ?? 'website'} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={socialTitle} />
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      {locale && <meta property="og:locale" content={locale} />}

      {/* Twitter Card — derived from the OpenGraph values. */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={socialTitle} />
      {description && <meta name="twitter:description" content={description} />}

      {/* OG + Twitter image metas (image / alt / dimensions). */}
      <SeoImageMeta image={{ ogImage, ogImageAlt, ogImageWidth, ogImageHeight }} />

      {alternates && (
        <>
          {Object.entries(alternates).map(([lang, href]) => (
            <link key={lang} rel="alternate" hrefLang={lang} href={href} />
          ))}
          {/* x-default → the default-locale (unprefixed) URL. Derived here at the
              single hreflang render point, so a page that emits per-locale
              alternates can never omit x-default by construction (ADR 0142). */}
          {alternates[DEFAULT_LOCALE] && (
            <link
              key="x-default"
              rel="alternate"
              hrefLang="x-default"
              href={alternates[DEFAULT_LOCALE]}
            />
          )}
        </>
      )}

      {/* Structured data (JSON-LD). Server-built JSON strings (ADR 0142); injected
          verbatim — the backend/SSOT is the trust boundary, not user input. */}
      {structuredData?.map((json, i) => (
        // eslint-disable-next-line no-restricted-syntax -- JSON-LD <script>, not user HTML: server-generated trusted JSON (ADR 0142 backend SSOT trust boundary); SafeHtml is for sanitized HTML body content and would corrupt the JSON
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />
      ))}
    </>
  );
}
